import { PROTOCOL } from './constants';

export interface LatLng {
  lat: number;
  lng: number;
}

const R_EARTH_M = 6_371_008.8;
const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/** Great-circle distance in metres (haversine). Good to well under 1 m at site scale. */
export function distanceM(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R_EARTH_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Initial compass bearing from a to b, 0–360, 0 = north. */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δλ = toRad(b.lng - a.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Eight-point compass label for the "near me" list. */
export function compassPoint(bearing: number): 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' {
  const pts = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
  return pts[Math.round((((bearing % 360) + 360) % 360) / 45) % 8];
}

/** The lead's mis-tag flag: the device was farther from the tree's stored point than the protocol allows. */
export function possibleMisTag(device: LatLng, tree: LatLng): boolean {
  return distanceM(device, tree) > PROTOCOL.MIS_TAG_DISTANCE_M;
}

/** Average of several fixes, weighted by 1/accuracy², for first registration of a tree. */
export function averageFix(fixes: ReadonlyArray<LatLng & { accuracyM: number }>): (LatLng & { accuracyM: number }) | null {
  if (fixes.length === 0) return null;
  let wSum = 0;
  let lat = 0;
  let lng = 0;
  for (const f of fixes) {
    const acc = Math.max(f.accuracyM, 1);
    const w = 1 / (acc * acc);
    wSum += w;
    lat += f.lat * w;
    lng += f.lng * w;
  }
  return { lat: lat / wSum, lng: lng / wSum, accuracyM: Math.sqrt(1 / wSum) };
}

/** Trees sorted by distance from the device, with bearing, for the "near me" list. */
export function nearest<T extends { position: LatLng }>(device: LatLng, trees: readonly T[], limit = 10) {
  return trees
    .map((t) => ({ tree: t, distanceM: distanceM(device, t.position), bearingDeg: bearingDeg(device, t.position) }))
    .sort((a, b) => a.distanceM - b.distanceM)
    .slice(0, limit);
}
